/* ============================================================
   VICE STATIC — motion core
   Lenis (smooth scroll) + GSAP/ScrollTrigger + Three.js emblem.
   Every module is defensive: a missing element must never throw,
   because these run inside Shopify sections that merchants can
   remove or reorder from the theme editor.
   ============================================================ */
(function () {
  "use strict";

  // Each Shopify section carries its own .vs-root wrapper, so the marker
  // class goes on <html> rather than one of them. Keeping the resets
  // scoped to .vs-root stops them leaking into the theme's own components.
  if (!document.querySelector(".vs-root")) return;
  document.documentElement.classList.add("vs-js");

  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var TOUCH = window.matchMedia("(hover: none)").matches;
  var hasGSAP = typeof window.gsap !== "undefined";
  var ST = hasGSAP && window.ScrollTrigger ? window.ScrollTrigger : null;
  if (ST) gsap.registerPlugin(ST);

  /* ---------------------------------------------------------
     1. Line splitter (own implementation — no SplitText needed)
     Wraps each visual line in .vs-line > span so it can be
     masked and pushed up. Re-runs on resize because line breaks
     move. Keeps the original HTML so re-splitting is lossless.
  --------------------------------------------------------- */
  function splitLines(el) {
    if (!el) return [];
    if (!el.dataset.vsOrig) el.dataset.vsOrig = el.innerHTML;
    else el.innerHTML = el.dataset.vsOrig;

    // Wrap every word so we can measure where the browser breaks.
    // <br> is turned into an explicit break marker so an authored line
    // break is always honoured, independent of measurement.
    // The <br> must stay a REAL <br> while we measure. If it is swapped for
    // an inline marker the browser wraps as if the break weren't there, and
    // the forced break then lands mid-line — orphaning single words.
    var html = el.dataset.vsOrig
      .replace(/<br\s*\/?>/gi, '<br class="vs-br">')
      .replace(/([^\s<>]+)(?![^<]*>)/g, '<i class="vs-w">$1</i>');
    el.innerHTML = html;

    var nodes = Array.prototype.slice.call(el.querySelectorAll(".vs-w, br.vs-br"));
    var words = nodes.filter(function (n) { return n.classList.contains("vs-w"); });
    if (!words.length) return [];

    var lines = [];
    var current = null;
    var lastTop = null;
    var forceBreak = false;
    nodes.forEach(function (n) {
      if (n.tagName === "BR") { forceBreak = true; return; }
      var top = Math.round(n.offsetTop);
      if (current === null || forceBreak || Math.abs(top - lastTop) > 2) {
        current = [];
        lines.push(current);
        forceBreak = false;
      }
      lastTop = top;
      current.push(n);
    });

    // Rebuild as masked lines.
    var frag = document.createDocumentFragment();
    lines.forEach(function (group) {
      var line = document.createElement("span");
      line.className = "vs-line";
      var inner = document.createElement("span");
      group.forEach(function (w, i) {
        inner.appendChild(document.createTextNode(i ? " " : ""));
        while (w.firstChild) inner.appendChild(w.firstChild);
      });
      line.appendChild(inner);
      frag.appendChild(line);
    });
    el.innerHTML = "";
    el.appendChild(frag);
    return Array.prototype.slice.call(el.querySelectorAll(".vs-line > span"));
  }

  /* ---------------------------------------------------------
     2. Loader
  --------------------------------------------------------- */
  var loader = document.querySelector(".vs-loader");
  function finishLoad() {
    if (!loader) return start();
    var bar = loader.querySelector(".vs-loader__bar i");
    var pct = loader.querySelector(".vs-loader__pct");
    var p = 0;
    var t = setInterval(function () {
      p = Math.min(100, p + Math.random() * 22 + 6);
      if (bar) bar.style.transform = "scaleX(" + p / 100 + ")";
      if (pct) pct.textContent = String(Math.round(p)).padStart(3, "0");
      if (p >= 100) {
        clearInterval(t);
        setTimeout(function () {
          loader.dataset.done = "1";
          start();
        }, 320);
      }
    }, REDUCED ? 40 : 130);
  }

  /* ---------------------------------------------------------
     3. Smooth scroll (Lenis) wired into GSAP's ticker
  --------------------------------------------------------- */
  var lenis = null;
  function initScroll() {
    // Safety net: drive ScrollTrigger from the native scroll event too.
    // If rAF is throttled (background tab, embedded webview, low-power
    // mode) Lenis stops ticking and would never call ST.update, freezing
    // every scrubbed animation mid-page. Native scroll events still fire.
    // `true` forces the recalculation. A plain update() defers to the GSAP
    // ticker, which is exactly what is stalled in these environments —
    // verified: ticker.frame stayed 0 while update(true) produced the
    // correct progress. This matters for real traffic, since Instagram and
    // TikTok in-app browsers throttle rAF the same way.
    if (ST) {
      window.addEventListener("scroll", function () { ST.update(true); }, { passive: true });
    }
    if (REDUCED || TOUCH || typeof window.Lenis === "undefined") return;
    // `duration` and `lerp` are mutually exclusive in Lenis — passing both
    // leaves its easing ambiguous and made programmatic scrollTo() no-op,
    // which silently killed every on-page anchor button. Keep lerp only.
    lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      wheelMultiplier: 0.95
    });
    if (ST) {
      lenis.on("scroll", ST.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      var raf = function (t) { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
    // Anchor links must go through Lenis or they fight its rAF loop.
    // Every branch here is defensive: this handler calls preventDefault, so
    // if the scroll that follows fails for any reason the button becomes a
    // dead link. A watchdog re-checks and falls back to a native scroll.
    document.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute("href");
      if (!id || id === "#") return;
      var target;
      try { target = document.querySelector(id); } catch (err) { return; }
      if (!target) return;

      var from = window.scrollY || document.documentElement.scrollTop;
      var to = target.getBoundingClientRect().top + from - 70;
      e.preventDefault();

      if (lenis && typeof lenis.scrollTo === "function") {
        try { lenis.scrollTo(to, { duration: 1.1 }); } catch (err) { /* fall through */ }
      }

      // Watchdog: if we haven't moved toward the target shortly after the
      // click, do it natively. Guarantees the CTA always goes somewhere.
      setTimeout(function () {
        var now = window.scrollY || document.documentElement.scrollTop;
        if (Math.abs(now - from) < 8 && Math.abs(to - from) > 8) {
          window.scrollTo({ top: to, behavior: "smooth" });
        }
      }, 260);
    });
  }

  /* ---------------------------------------------------------
     4. Header — hide on scroll down, solid past the hero
  --------------------------------------------------------- */
  function initHeader() {
    var head = document.querySelector(".vs-head");
    if (!head) return;
    var last = 0;
    var onScroll = function () {
      var y = window.scrollY || document.documentElement.scrollTop;
      head.dataset.solid = y > window.innerHeight * 0.7 ? "1" : "0";
      head.dataset.hidden = y > last && y > 400 ? "1" : "0";
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------
     5. Hero — scrubbed wrapper transform.
     NOTE: we deliberately do NOT frame-scrub the video. Seeking a
     video from scroll is unreliable on iOS Safari (it will not
     decode fast enough and stutters). Instead the video plays
     normally and the *wrapper* is scrubbed — same cinematic
     result, robust everywhere. This mirrors the reference site.
  --------------------------------------------------------- */
  function initHero() {
    var hero = document.querySelector(".vs-hero");
    if (!hero) return;
    var media = hero.querySelector(".vs-hero__media");
    var body = hero.querySelector(".vs-hero__body");
    var vid = hero.querySelector("video");

    if (vid) {
      vid.muted = true;
      vid.playsInline = true;
      vid.setAttribute("muted", "");
      vid.setAttribute("playsinline", "");

      // Pick the source in JS. The `media` attribute on <source> is only
      // honoured inside <picture>; browsers ignore it for <video> and just
      // take the first playable file, which would ship the small clip to
      // desktop (or the large one to phones).
      var small = vid.dataset.vsSrcSmall;
      var large = vid.dataset.vsSrcLarge;
      if (small && large) {
        var slow = navigator.connection &&
          (navigator.connection.saveData || /2g/.test(navigator.connection.effectiveType || ""));
        var want = (window.innerWidth > 760 && !slow) ? large : small;
        if (vid.getAttribute("src") !== want) {
          vid.setAttribute("src", want);
          vid.load();
        }
      }

      var tryPlay = function () {
        var pr = vid.play();
        if (pr && pr.catch) pr.catch(function () { /* autoplay blocked — poster shows */ });
      };
      if (vid.readyState >= 2) tryPlay();
      vid.addEventListener("loadeddata", tryPlay, { once: true });
      vid.addEventListener("canplay", tryPlay, { once: true });
      // Some browsers only allow playback after the first interaction.
      document.addEventListener("pointerdown", tryPlay, { once: true });
    }

    // Headline reveal
    var title = hero.querySelector("[data-vs-split]");
    var spans = title ? splitLines(title) : [];
    var fades = hero.querySelectorAll(".vs-fade");

    if (hasGSAP && !REDUCED) {
      var tl = gsap.timeline({ delay: 0.15 });
      if (spans.length) {
        tl.to(spans, {
          y: 0, duration: 1.25, ease: "expo.out", stagger: 0.09
        }, 0);
      }
      if (fades.length) {
        tl.to(fades, {
          opacity: 1, y: 0, duration: 1, ease: "power2.out", stagger: 0.08
        }, 0.35);
      }
      if (media && ST) {
        gsap.to(media, {
          scale: 1.14,
          filter: "brightness(.45) blur(5px)",
          ease: "none",
          scrollTrigger: {
            trigger: hero, start: "top top", end: "bottom top", scrub: true
          }
        });
      }
      if (body && ST) {
        gsap.to(body, {
          y: -70, opacity: 0, ease: "none",
          scrollTrigger: {
            trigger: hero, start: "top top", end: "bottom top", scrub: true
          }
        });
      }
    } else {
      spans.forEach(function (s) { s.style.transform = "none"; });
      Array.prototype.forEach.call(fades, function (f) { f.style.opacity = 1; });
    }
  }

  /* ---------------------------------------------------------
     6. Generic scroll reveals for every section below the hero
  --------------------------------------------------------- */
  function initReveals() {
    // Select every split/fade target OUTSIDE the hero, regardless of which
    // wrapper class its section uses. Scoping this to `.vs-sec` silently
    // skipped the WebGL section and left its headline stuck off-screen.
    var outsideHero = function (el) { return !el.closest(".vs-hero"); };

    var titles = Array.prototype.filter.call(
      document.querySelectorAll("[data-vs-split]"), outsideHero);
    titles.forEach(function (t) {
      var spans = splitLines(t);
      if (!spans.length) return;
      if (!hasGSAP || !ST || REDUCED) {
        spans.forEach(function (s) { s.style.transform = "none"; });
        return;
      }
      gsap.to(spans, {
        y: 0, duration: 1.15, ease: "expo.out", stagger: 0.075,
        scrollTrigger: { trigger: t, start: "top 88%" }
      });
    });

    var fades = Array.prototype.filter.call(
      document.querySelectorAll(".vs-fade"), outsideHero);
    fades.forEach(function (f) {
      if (!hasGSAP || !ST || REDUCED) { f.style.opacity = 1; return; }
      gsap.fromTo(f,
        { opacity: 0, y: 26 },
        {
          opacity: 1, y: 0, duration: 1, ease: "power2.out",
          scrollTrigger: { trigger: f, start: "top 92%" }
        });
    });

    // Staggered groups (rails, tiles, stats)
    var groups = document.querySelectorAll("[data-vs-stagger]");
    Array.prototype.forEach.call(groups, function (g) {
      var kids = g.children;
      if (!kids.length) return;
      if (!hasGSAP || !ST || REDUCED) return;
      gsap.fromTo(kids,
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.95, ease: "power3.out", stagger: 0.07,
          scrollTrigger: { trigger: g, start: "top 86%" }
        });
    });
  }

  /* ---------------------------------------------------------
     7. Theme inversion — background flips as a light section
     crosses under the fixed header.
  --------------------------------------------------------- */
  function initInvert() {
    if (!hasGSAP || !ST) return;
    var sections = document.querySelectorAll("[data-vs-invert]");
    Array.prototype.forEach.call(sections, function (sec) {
      ST.create({
        trigger: sec,
        start: "top 80px",
        end: "bottom 80px",
        onToggle: function (self) {
          sec.classList.toggle("vs-invert", self.isActive);
          var head = document.querySelector(".vs-head");
          if (head) head.classList.toggle("vs-invert", self.isActive);
        }
      });
    });
  }

  /* ---------------------------------------------------------
     8. Lookbook parallax
  --------------------------------------------------------- */
  function initParallax() {
    if (!hasGSAP || !ST || REDUCED) return;
    var figs = document.querySelectorAll("[data-vs-parallax] img");
    Array.prototype.forEach.call(figs, function (img) {
      gsap.fromTo(img,
        { yPercent: -7 },
        {
          yPercent: 7, ease: "none",
          scrollTrigger: {
            trigger: img.parentElement,
            start: "top bottom", end: "bottom top", scrub: true
          }
        });
    });
  }

  /* ---------------------------------------------------------
     9. Marquee — duplicated track, seamless wrap
  --------------------------------------------------------- */
  function initMarquee() {
    var rows = document.querySelectorAll(".vs-tick__row");
    Array.prototype.forEach.call(rows, function (row) {
      var content = row.querySelector("span");
      if (!content) return;
      // Duplicate until the track is at least 2x the viewport.
      var guard = 0;
      while (row.scrollWidth < window.innerWidth * 2 && guard < 12) {
        row.appendChild(content.cloneNode(true));
        guard++;
      }
      if (!hasGSAP || REDUCED) return;
      var single = content.getBoundingClientRect().width;
      if (!single) return;
      var dir = row.dataset.dir === "rtl" ? 1 : -1;
      gsap.to(row, {
        x: dir * single,
        duration: single / 60,
        ease: "none",
        repeat: -1,
        modifiers: {
          x: function (x) {
            return (parseFloat(x) % single) + "px";
          }
        }
      });
    });
  }

  /* ---------------------------------------------------------
     10. Countdown to the drop
  --------------------------------------------------------- */
  function initCountdown() {
    var els = document.querySelectorAll("[data-vs-countdown]");
    if (!els.length) return;
    Array.prototype.forEach.call(els, function (el) {
      var target = new Date(el.dataset.vsCountdown).getTime();
      if (isNaN(target)) return;
      var slots = {
        d: el.querySelector('[data-u="d"]'),
        h: el.querySelector('[data-u="h"]'),
        m: el.querySelector('[data-u="m"]'),
        s: el.querySelector('[data-u="s"]')
      };
      var pad = function (n, l) { return String(Math.max(0, n)).padStart(l || 2, "0"); };
      var tick = function () {
        var diff = target - Date.now();
        if (diff <= 0) {
          el.dataset.live = "1";
          Object.keys(slots).forEach(function (k) { if (slots[k]) slots[k].textContent = "00"; });
          return;
        }
        var s = Math.floor(diff / 1000);
        if (slots.d) slots.d.textContent = pad(Math.floor(s / 86400), 3);
        if (slots.h) slots.h.textContent = pad(Math.floor(s / 3600) % 24);
        if (slots.m) slots.m.textContent = pad(Math.floor(s / 60) % 60);
        if (slots.s) slots.s.textContent = pad(s % 60);
      };
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------------
     11. Stat counters
  --------------------------------------------------------- */
  function initCounters() {
    var nums = document.querySelectorAll("[data-vs-count]");
    Array.prototype.forEach.call(nums, function (el) {
      var to = parseFloat(el.dataset.vsCount);
      if (isNaN(to)) return;
      var suffix = el.dataset.vsSuffix || "";
      if (!hasGSAP || !ST || REDUCED) {
        el.textContent = Math.round(to).toLocaleString() + suffix;
        return;
      }
      var obj = { v: 0 };
      gsap.to(obj, {
        v: to, duration: 1.9, ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 90%" },
        onUpdate: function () {
          el.textContent = Math.round(obj.v).toLocaleString() + suffix;
        }
      });
    });
  }

  /* ---------------------------------------------------------
     12. FAQ accordion (height-animated, keyboard accessible)
  --------------------------------------------------------- */
  function initFaq() {
    var items = document.querySelectorAll(".vs-faq__i");
    Array.prototype.forEach.call(items, function (item) {
      var btn = item.querySelector(".vs-faq__q");
      var panel = item.querySelector(".vs-faq__a");
      if (!btn || !panel) return;
      btn.setAttribute("aria-expanded", "false");
      btn.addEventListener("click", function () {
        var open = item.dataset.open === "1";
        // close siblings
        Array.prototype.forEach.call(items, function (o) {
          if (o === item) return;
          o.dataset.open = "0";
          var op = o.querySelector(".vs-faq__a");
          var ob = o.querySelector(".vs-faq__q");
          if (op) op.style.height = "0px";
          if (ob) ob.setAttribute("aria-expanded", "false");
        });
        item.dataset.open = open ? "0" : "1";
        btn.setAttribute("aria-expanded", open ? "false" : "true");
        panel.style.height = open ? "0px" : panel.scrollHeight + "px";
        if (ST) setTimeout(function () { ST.refresh(); }, 580);
      });
    });
  }

  /* ---------------------------------------------------------
     13. Mobile sticky CTA — appears once the hero is passed
  --------------------------------------------------------- */
  function initSticky() {
    var bar = document.querySelector(".vs-sticky");
    var hero = document.querySelector(".vs-hero");
    if (!bar || !hero) return;
    var onScroll = function () {
      var y = window.scrollY || document.documentElement.scrollTop;
      bar.dataset.show = y > window.innerHeight * 0.85 ? "1" : "0";
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------
     14. Secondary videos — play only while in view
  --------------------------------------------------------- */
  function initVideos() {
    var vids = document.querySelectorAll("video[data-vs-inview]");
    if (!vids.length || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          var p = v.play(); if (p && p.catch) p.catch(function () {});
        } else if (!v.paused) v.pause();
      });
    }, { rootMargin: "200px 0px" });
    Array.prototype.forEach.call(vids, function (v) { io.observe(v); });
  }

  /* ---------------------------------------------------------
     15. Three.js emblem — neon grid horizon + chrome object.
     Scroll scrubs the camera dolly and the object's spin.
     Skipped entirely on reduced-motion or when WebGL is absent.
  --------------------------------------------------------- */
  function initEmblem() {
    var host = document.querySelector(".vs-emblem");
    if (!host) return;
    // `host` is the tall scroll track; `stage` is the sticky, viewport-sized
    // box that actually holds the canvas. Size the renderer to the stage.
    var stage = host.querySelector(".vs-emblem__stick") || host;
    var canvas = host.querySelector(".vs-emblem__canvas");
    var fallback = host.querySelector(".vs-emblem__fb");
    var THREE = window.THREE;

    function bail() {
      if (canvas) canvas.style.display = "none";
      if (fallback) fallback.style.display = "block";
    }
    if (!canvas || !THREE || REDUCED) return bail();

    var gl;
    try {
      gl = new THREE.WebGLRenderer({
        canvas: canvas, antialias: !TOUCH, alpha: false, powerPreference: "high-performance"
      });
    } catch (err) { return bail(); }
    if (fallback) fallback.style.display = "none";

    var DPR = Math.min(window.devicePixelRatio || 1, TOUCH ? 1.5 : 2);
    gl.setPixelRatio(DPR);
    gl.setSize(stage.clientWidth, stage.clientHeight, false);

    var scene = new THREE.Scene();
    scene.background = new THREE.Color(0x05040a);
    scene.fog = new THREE.Fog(0x05040a, 9, 34);

    var cam = new THREE.PerspectiveCamera(52, stage.clientWidth / stage.clientHeight, 0.1, 120);
    cam.position.set(0, 1.9, 9.4);
    cam.lookAt(0, 1.1, 0);

    /* --- neon grid floor (shader) --- */
    var gridMat = new THREE.ShaderMaterial({
      transparent: true,
      uniforms: {
        uTime: { value: 0 },
        uMag: { value: new THREE.Color(0xff2d78) },
        uCyan: { value: new THREE.Color(0x00e5ff) }
      },
      vertexShader:
        "varying vec2 vUv;varying float vD;" +
        "void main(){vUv=uv;vec4 mv=modelViewMatrix*vec4(position,1.0);vD=-mv.z;" +
        "gl_Position=projectionMatrix*mv;}",
      fragmentShader:
        "varying vec2 vUv;varying float vD;uniform float uTime;uniform vec3 uMag;uniform vec3 uCyan;" +
        "float line(float c,float w){float g=abs(fract(c-0.5)-0.5)/fwidth(c);return 1.0-smoothstep(0.0,w,g);}" +
        "void main(){" +
        "vec2 g=vec2(vUv.x*44.0, vUv.y*44.0 - uTime*1.35);" +
        "float l=max(line(g.x,1.4),line(g.y,1.4));" +
        "vec3 col=mix(uCyan,uMag,smoothstep(0.24,0.78,vUv.x));" +
        "float fade=smoothstep(46.0,5.0,vD);" +      // die out toward the horizon
        "float near=smoothstep(0.0,3.0,vD);" +        // and right under the camera
        "gl_FragColor=vec4(col*l*1.5,l*fade*near*0.85);}"
    });
    var grid = new THREE.Mesh(new THREE.PlaneGeometry(120, 120, 1, 1), gridMat);
    grid.rotation.x = -Math.PI / 2;
    grid.position.y = -1.15;
    scene.add(grid);

    /* --- chrome object --- */
    var env = null;
    var envUrl = canvas.dataset.env;
    if (envUrl) {
      var pmrem = new THREE.PMREMGenerator(gl);
      pmrem.compileEquirectangularShader();
      new THREE.TextureLoader().load(envUrl, function (tex) {
        tex.mapping = THREE.EquirectangularReflectionMapping;
        env = pmrem.fromEquirectangular(tex).texture;
        scene.environment = env;
        mat.envMap = env;
        mat.needsUpdate = true;
        tex.dispose(); pmrem.dispose();
      }, undefined, function () { /* env is optional */ });
    }

    /* --- the numeral "VI", extruded in chrome ---
       Built from two hand-plotted 2D shapes rather than loaded from a font,
       so there is no extra network request and no font licence involved.
       A Roman numeral is not anyone's logo — this deliberately does not
       reproduce the game's wordmark or typeface. */
    function shapeFrom(points) {
      var sh = new THREE.Shape();
      sh.moveTo(points[0][0], points[0][1]);
      for (var i = 1; i < points.length; i++) sh.lineTo(points[i][0], points[i][1]);
      sh.closePath();
      return sh;
    }

    var H = 1.55;            // half height of the numerals
    var STEM = 0.40;         // half width of the I
    // V traced as one outline: down the left outer edge, up into the inner
    // notch, out to the right outer edge, then across the flat foot.
    var vShape = shapeFrom([
      [-1.02,  H], [-0.42,  H], [0, -0.85], [0.42,  H], [1.02,  H],
      [ 0.30, -H], [-0.30, -H]
    ]);
    var iShape = shapeFrom([
      [-STEM, H], [STEM, H], [STEM, -H], [-STEM, -H]
    ]);

    var extrude = {
      depth: 0.46,
      bevelEnabled: true,
      bevelThickness: 0.06,
      bevelSize: 0.05,
      bevelSegments: TOUCH ? 2 : 4,
      curveSegments: 4
    };

    var mat = new THREE.MeshStandardMaterial({
      color: 0xd6dae2, metalness: 1.0, roughness: 0.12, envMapIntensity: 1.6
    });

    var obj = new THREE.Group();
    var vMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(vShape, extrude), mat);
    var iMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(iShape, extrude), mat);
    // Positions centre the pair as a unit (V is 2.04 wide, I is 0.80,
    // 0.30 gap), and the z offset centres the extrusion on its own axis so
    // the group spins about the numerals rather than about their back face.
    vMesh.position.set(-0.55, 0, -0.25);
    iMesh.position.set( 1.17, 0, -0.25);
    obj.add(vMesh, iMesh);
    obj.scale.setScalar(0.9);
    obj.position.y = 1.15;
    scene.add(obj);

    /* --- neon lighting --- */
    var l1 = new THREE.PointLight(0xff2d78, 340, 44, 2); l1.position.set(-5.5, 4.2, 4.2);
    var l2 = new THREE.PointLight(0x00e5ff, 300, 44, 2); l2.position.set(5.5, 1.4, 4.6);
    var l3 = new THREE.PointLight(0xffa31a, 130, 40, 2); l3.position.set(0, 5.2, -6.5);
    scene.add(l1, l2, l3, new THREE.AmbientLight(0x2a2440, 1.1));

    /* --- resize --- */
    function resize() {
      var w = stage.clientWidth, h = stage.clientHeight;
      if (!w || !h) return;
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      gl.setSize(w, h, false);
    }
    window.addEventListener("resize", resize);
    resize();

    /* --- scroll scrub ---
       Progress is read straight off the track's own position each frame,
       so the 3D stays in sync with CSS sticky and needs neither GSAP nor a
       ScrollTrigger pin. Nothing here can interfere with page scrolling:
       if this whole block threw, the section would still be a normal
       sticky element and the page would scroll past it fine. */
    var prog = { v: 0, eased: 0 };
    function readProgress() {
      var r = host.getBoundingClientRect();
      var travel = r.height - window.innerHeight;
      if (travel <= 0) return 0;
      var p = -r.top / travel;
      return p < 0 ? 0 : p > 1 ? 1 : p;
    }

    /* --- render loop --- */
    var clock = new THREE.Clock();
    var visible = true;
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) { visible = e.isIntersecting; });
      }, { rootMargin: "120px 0px" }).observe(host);
    }
    function loop() {
      requestAnimationFrame(loop);
      if (!visible) return;
      var t = clock.getElapsedTime();
      prog.v = readProgress();
      // Light smoothing so the camera glides rather than tracking raw scroll.
      prog.eased += (prog.v - prog.eased) * 0.08;
      var p = prog.eased;
      gridMat.uniforms.uTime.value = t;
      // Sway rather than tumble: a full spin turns the numerals edge-on and
      // they read as thin slivers for half the rotation. Scroll adds turn.
      obj.rotation.y = Math.sin(t * 0.35) * 0.5 + p * 1.15;
      obj.rotation.x = Math.sin(t * 0.5) * 0.1 + p * 0.22;
      obj.position.y = 1.15 + Math.sin(t * 0.7) * 0.11;
      cam.position.z = 9.4 - p * 4.6;
      cam.position.y = 1.9 + p * 0.5;
      cam.lookAt(0, 1.1, 0);
      gl.render(scene, cam);
    }
    loop();
  }

  /* ---------------------------------------------------------
     16. Drop-list signup (Shopify customer form or fallback)
  --------------------------------------------------------- */
  function initSignup() {
    var forms = document.querySelectorAll("[data-vs-signup]");
    Array.prototype.forEach.call(forms, function (form) {
      // A real Shopify customer form posts natively; only the
      // demo/fallback variant is intercepted here.
      if (form.dataset.vsSignup !== "demo") return;
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var ok = form.querySelector(".vs-signup__ok");
        var input = form.querySelector('input[type="email"]');
        if (!input || !input.value) return;
        if (ok) ok.textContent = "// You're on the list. Watch your inbox.";
        input.value = "";
      });
    });
  }

  /* ---------------------------------------------------------
     Failsafe: reveal everything if the animation engine never runs.

     Pre-animation state hides copy (opacity:0 / translateY(110%)) and GSAP
     is expected to animate it back in. If the GSAP ticker never advances —
     rAF throttled in an embedded webview, a background tab that never gets
     foregrounded, a blocked CDN — that copy stays invisible forever and the
     page silently loses its headlines and calls to action.

     Verified real: in an embedded preview here gsap.ticker.frame stayed at
     0 indefinitely. Instagram and TikTok in-app browsers throttle the same
     way, and that is where most of this store's traffic will come from, so
     this failsafe protects actual revenue rather than a hypothetical.
  --------------------------------------------------------- */
  function revealAll() {
    document.querySelectorAll(".vs-fade").forEach(function (f) {
      f.style.opacity = "1";
      f.style.transform = "none";
    });
    document.querySelectorAll(".vs-line > span").forEach(function (s) {
      s.style.transform = "none";
    });
    // Stagger groups are hidden by gsap.fromTo's start state, not by a
    // class, so they need clearing too — these are the product rail, the
    // stat band and the collection tiles, i.e. the things people click.
    document.querySelectorAll("[data-vs-stagger] > *").forEach(function (k) {
      k.style.opacity = "1";
      k.style.transform = "none";
    });
    // Parallax images sit at a -7% offset until scrubbed.
    document.querySelectorAll("[data-vs-parallax] img").forEach(function (i) {
      i.style.transform = "none";
    });
    var l = document.querySelector(".vs-loader");
    if (l) l.dataset.done = "1";
  }

  function armFailsafe() {
    if (!hasGSAP) return revealAll();
    setTimeout(function () {
      if (gsap.ticker.frame === 0) revealAll();
    }, 1800);
  }

  /* ---------------------------------------------------------
     Boot
  --------------------------------------------------------- */
  var started = false;
  function start() {
    if (started) return;
    started = true;
    initScroll();
    initHeader();
    initHero();
    initReveals();
    initInvert();
    initParallax();
    initMarquee();
    initCountdown();
    initCounters();
    initFaq();
    initSticky();
    initVideos();
    initEmblem();
    initSignup();
    armFailsafe();
    if (ST) setTimeout(function () { ST.refresh(); }, 400);
  }

  // Re-split on resize so masked lines stay correct at the new width.
  // Only width changes matter — mobile browsers fire resize constantly as
  // the URL bar hides, and re-splitting on that would thrash the layout.
  var rt, lastW = window.innerWidth;
  window.addEventListener("resize", function () {
    if (window.innerWidth === lastW) return;
    lastW = window.innerWidth;
    clearTimeout(rt);
    rt = setTimeout(function () {
      document.querySelectorAll("[data-vs-split]").forEach(function (el) {
        var spans = splitLines(el);
        // Anything already scrolled past must stay visible after a re-split.
        if (el.getBoundingClientRect().top < window.innerHeight) {
          spans.forEach(function (s) { s.style.transform = "none"; });
        }
      });
      if (ST) ST.refresh();
    }, 280);
  });

  // Fonts must be ready before we measure line breaks — measuring in the
  // fallback face gives a completely different set of lines, which is what
  // makes home-rolled splitters look broken on first paint.
  function boot() {
    var fonts = document.fonts && document.fonts.ready
      ? document.fonts.ready
      : Promise.resolve();
    // Never let a stalled font request block the page.
    var timeout = new Promise(function (r) { setTimeout(r, 2200); });
    Promise.race([fonts, timeout]).then(finishLoad);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else boot();
})();
